import {
  ENGLISH_UK_LANGUAGE_ID,
  langToLocaleDict,
  SUPPORTED_LANGUAGES,
} from './constants';
import {validationLabels, errorMessages} from './translations';
import {
  CombinedCountryData,
  Country,
  PortalLanguage,
  SignupForm,
} from './types';
import {L10n, useGlobalization} from '@planday-corp/globalization';
import {ValidateFn} from '@planday-corp/react-model';
import {orderBy} from 'lodash';
import {useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import isEmail from 'sane-email-validation';

export const validationRules = {
  email: <T extends object, P extends keyof T>(): ValidateFn<T, P> => {
    return (model, prop) => {
      const value = model[prop];

      if (typeof value === 'string' && !isEmail(value)) {
        return validationLabels.email.invalid;
      }
    };
  },
  password: <T extends object, P extends keyof T>(): ValidateFn<T, P> => {
    return (model, prop) => {
      const value = model[prop];

      if (typeof value === 'string' && value.length < 8) {
        return validationLabels.password;
      }
    };
  },
  phone: <T extends object, P extends keyof T>(): ValidateFn<T, P> => {
    return (model, prop) => {
      const value = model[prop];

      if (isNaN(Number(value))) {
        return validationLabels.phone;
      }
    };
  },
  emailInUse: (isUsed?: boolean) => {
    return () => {
      if (isUsed === true) {
        return validationLabels.email.inUse;
      }
    };
  },
  termsAndConditions: <T extends object, P extends keyof T>(): ValidateFn<
    T,
    P
  > => {
    return (model, prop) => {
      const value = model[prop];

      if (value !== true) {
        return errorMessages.required;
      }
    };
  },
};

export const localizeDefaultData = ({
  countriesPhoneData,
  portalLanguages,
  update,
  l10n,
}: {
  countriesPhoneData: CombinedCountryData[];
  portalLanguages: PortalLanguage[];
  update: (value: string | number, name: keyof SignupForm) => void;
  l10n: L10n;
}) => {
  if (!countriesPhoneData?.length || !portalLanguages?.length) {
    return;
  }

  let currentCountryCode = l10n.locale.split('-')[1].toUpperCase();

  if (currentCountryCode === 'GB') {
    currentCountryCode = 'UK';
  }

  const matchingCountry = countriesPhoneData.find(
    country => country.countryCode.toUpperCase() === currentCountryCode
  );

  const matchingLanguage = portalLanguages.find(
    language =>
      language.uiCultureName.toLocaleLowerCase() ===
      l10n.locale.toLocaleLowerCase()
  )?.id;

  if (matchingCountry) {
    update(matchingCountry.prefixNumber, 'phoneCountryCodePrefix');
    update(matchingCountry.countryCode, 'countryCode');
  }

  if (matchingLanguage) {
    update(matchingLanguage, 'portalLanguageId');
  } else {
    update(ENGLISH_UK_LANGUAGE_ID, 'portalLanguageId');
  }
};

export const useMapAndSortCountries = (countries?: Country[]) => {
  const {i18n} = useGlobalization();
  return useMemo(
    () =>
      countries
        ? orderBy(countries, ['name'], ['asc']).map(country => ({
            value: country.code,
            label: i18n.t({
              term: `Countries.${country.name}`,
              fallback: country.name,
            }),
          }))
        : [],
    [countries, i18n]
  );
};

const sortIgnoringSpecialCharacters = (a: string, b: string): number => {
  return a.localeCompare(b, 'en', {sensitivity: 'base'});
};

export const usePhonePrefixOptions = (
  countriesPhoneData?: CombinedCountryData[]
) => {
  const {i18n} = useGlobalization();
  return useMemo(() => {
    if (!countriesPhoneData?.length) {
      return [];
    }
    const sortedData = [...countriesPhoneData].sort((a, b) =>
      sortIgnoringSpecialCharacters(
        i18n.t(`Countries.${a.countryCode}`),
        i18n.t(`Countries.${b.countryCode}`)
      )
    );
    return sortedData.map(item => ({
      label: `${i18n.t({
        term: `Countries.${item.countryCode}`,
        fallback: item.countryName,
      })}`,
      value: item.prefixNumber,
    }));
  }, [countriesPhoneData, i18n]);
};

export const getLangFromUrl = () => {
  const path = window.location.pathname;
  const urlLanguage = path
    .split('/')
    ?.find(p => SUPPORTED_LANGUAGES.includes(p));

  if (urlLanguage) {
    return urlLanguage;
  } else {
    return new Intl.Locale(navigator?.language)?.language || 'en';
  }
};

export const getLocaleFromUrl = () => langToLocaleDict[getLangFromUrl()];

export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export const useNormalizedQueryParams = (keysToPick?: string[]) => {
  const [searchParams] = useSearchParams();
  const paramsDictionary = Object.fromEntries(
    new URLSearchParams(searchParams)
  );

  return Object.keys(paramsDictionary)
    .filter(k => !keysToPick || keysToPick.includes(k))
    .reduce(
      (acc: Record<string, string>, k) => {
        acc[snakeToCamel(k)] = paramsDictionary[k];
        return acc;
      },
      {} as Record<string, string>
    );
};

export const removeExtraSpaces = (text: string) =>
  text.trim().split(/ +/).join(' ');
